export interface PathsManifest {
  [key: string]: string | ((...params: Array<unknown>) => string | undefined) | PathsManifest | undefined;
}

export const routeConfig: {
  port: number;
  fetchRoutesMsg: string;
  browserRoutes: Record<string, string>;
  nodeRoutes: Record<string, Record<string, boolean>>;
} = {
  port: 7651,
  fetchRoutesMsg: 'fetchNodeRoutes',
  browserRoutes: {},
  nodeRoutes: {},
};

export function resetRoutes() {
  routeConfig.browserRoutes = {};
  routeConfig.nodeRoutes = {};
}

export function parseManifestRoutes(
  pathsManifest: PathsManifest,
  ...params: Array<unknown>
): typeof routeConfig.browserRoutes {
  const routes: Record<string, string> = {};
  Object.keys(pathsManifest).forEach((key) => {
    let value = pathsManifest[key];
    if (value) {
      if (typeof value === 'string') {
        routes[key] = value;
      } else if (typeof value === 'function') {
        value = value(...params);
        if (value) {
          routes[key] = value;
        }
      } else if (value.constructor === Object) {
        const subRoutes = parseManifestRoutes(value, ...params);
        Object.keys(subRoutes).forEach((subKey) => {
          routes[`${key}.${subKey}`] = subRoutes[subKey];
        });
      } else {
        try {
          routes[key] = value.toString();
        } catch {
          routes[key] = `[ failed to parse ${typeof value} ]`;
        }
      }
    }
  });
  return routes;
}

export function registerBrowserRoutes(pathsManifest: PathsManifest, ...params: Array<unknown>) {
  const parsedRoutes = parseManifestRoutes(pathsManifest, ...params);
  Object.assign(routeConfig.browserRoutes, parsedRoutes);
}

export async function getBrowserRoutes(): Promise<typeof routeConfig.browserRoutes> {
  return routeConfig.browserRoutes;
}

export function makeRouteFetcher(nodeSocketApi?: string): Promise<() => Promise<typeof routeConfig.nodeRoutes>> {
  const client = new WebSocket(nodeSocketApi || location.origin.replace(/^http/, 'ws'));
  let handleMessage: (message: typeof routeConfig.nodeRoutes) => void;
  function fetchRoutes(): Promise<typeof routeConfig.nodeRoutes> {
    client.send(routeConfig.fetchRoutesMsg);
    return new Promise((resolve) => {
      handleMessage = resolve;
    });
  }
  client.addEventListener('message', ({ data }) => {
    if (handleMessage) {
      handleMessage(JSON.parse(data));
    }
  });
  return new Promise((resolve) => {
    client.addEventListener('open', () => {
      resolve(fetchRoutes);
    });
  });
}

let fetchRoutes: () => Promise<typeof routeConfig.nodeRoutes>;

export async function getNodeRoutes(nodeSocketApi?: string): Promise<typeof routeConfig.nodeRoutes> {
  try {
    fetchRoutes = fetchRoutes || (await makeRouteFetcher(nodeSocketApi));
    return fetchRoutes();
  } catch {
    return {};
  }
}
